"use client";

import { t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";
import type { WizardState } from "./types";

interface Step4Props {
    state: WizardState;
}

export default function Step4Review({ state }: Step4Props) {
    const { locale } = useLocale();
    if (!state.mode) return null;
    const modeLabel = t(`avatars.wizard.step4.mode.${state.mode}`, locale);
    const isPrompt = state.mode === "prompt";
    const mediaLabel = t(
        isPrompt ? "avatars.wizard.step4.prompt" : "avatars.wizard.step4.media",
        locale
    );

    return (
        <div className="flex flex-col gap-4">
            <div className="rounded-xl border bg-muted/30 p-4 flex flex-col gap-3">
                <Section label={t("avatars.wizard.step4.mode", locale)}>
                    <span className="text-sm font-medium">{modeLabel}</span>
                </Section>

                <div className="border-t" />

                <Section label={t("avatars.wizard.step4.name", locale)}>
                    <span className="text-sm font-medium">{state.name}</span>
                </Section>

                <div className="border-t" />

                <Section label={mediaLabel}>
                    {state.mode === "photo" && state.sourcePreviewUrl && (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                            src={state.sourcePreviewUrl}
                            alt="source"
                            className="w-40 h-40 rounded-lg object-cover border"
                        />
                    )}
                    {state.mode === "video" && state.sourcePreviewUrl && (
                        <video
                            src={state.sourcePreviewUrl}
                            className="w-full aspect-video rounded-lg object-cover border"
                            controls
                        />
                    )}
                    {isPrompt && (
                        <p className="text-sm font-medium whitespace-pre-wrap">
                            {state.prompt}
                        </p>
                    )}
                </Section>

                {isPrompt && state.referenceImages.length > 0 && (
                    <>
                        <div className="border-t" />
                        <Section
                            label={t(
                                "avatars.wizard.step4.references",
                                locale
                            )}
                        >
                            <div className="flex flex-wrap gap-2">
                                {state.referenceImages.map((img, i) => (
                                    // eslint-disable-next-line @next/next/no-img-element
                                    <img
                                        key={i}
                                        src={img.previewUrl}
                                        alt=""
                                        className="size-14 rounded-md object-cover border"
                                    />
                                ))}
                            </div>
                        </Section>
                    </>
                )}
            </div>

            {state.mode === "video" && (
                <p className="text-xs text-muted-foreground">
                    {t("avatars.wizard.step4.note.video", locale)}
                </p>
            )}
        </div>
    );
}

function Section({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) {
    return (
        <div className="flex flex-col gap-1">
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                {label}
            </span>
            {children}
        </div>
    );
}
